import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from '../supabaseClient.js';
import { SUBSCRIPTION_PLANS } from '../constants/subscriptionPlans.js';

const WalletBalanceContext = createContext({
  wallet: null,
  planTier: null,
  loading: false,
  refreshWallet: async () => null,
});

const isNewerWallet = (incoming, current) => {
  if (!current) return true;
  if (Number(incoming.version) !== Number(current.version)) {
    return Number(incoming.version) > Number(current.version);
  }
  return new Date(incoming.updated_at).getTime() >= new Date(current.updated_at).getTime();
};

export const creditWalletOperationCompleted = () => {
  window.dispatchEvent(new Event('credit-wallet-operation-complete'));
};

export const WalletBalanceProvider = ({ children }) => {
  const { userDetails } = useSelector(
    (state) => ({ userDetails: state.globalState.userDetails }),
    shallowEqual,
  );
  const userId = userDetails?.id;
  const [wallet, setWallet] = useState(null);
  // The subscription tier (credit_tier || plan_name), used to derive the
  // plan's monthly credit allowance — the denominator for sidebar meters.
  // The wallet row's allotment_halves is the REMAINING allotment this cycle,
  // not the allowance, so the plan total must come from subscription_info.
  const [planTier, setPlanTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const walletRef = useRef(null);
  const planTierRef = useRef(null);
  const currentUserIdRef = useRef(userId);
  currentUserIdRef.current = userId;

  const acceptWallet = useCallback((nextWallet) => {
    if (!nextWallet || !isNewerWallet(nextWallet, walletRef.current)) return false;
    walletRef.current = nextWallet;
    setWallet(nextWallet);
    return true;
  }, []);

  const refreshWallet = useCallback(async () => {
    if (!userId) {
      walletRef.current = null;
      setWallet(null);
      return null;
    }

    setLoading(true);
    try {
      // RLS permits authenticated users to read only their own wallet row. Do not
      // use the service-only wallet state RPC in the browser.
      const { data, error } = await supabase
        .from('therapist_credit_wallets')
        .select('user_id, balance_halves, allotment_halves, version, updated_at, allotment_expires_at')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      // A late request for a previous login must never replace this user's
      // balance, even if its version happens to be higher.
      if (currentUserIdRef.current !== userId) return null;
      if (data) acceptWallet(data);
      else {
        walletRef.current = null;
        setWallet(null);
      }
      return data || null;
    } catch (error) {
      console.error('Unable to refresh credit wallet:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [acceptWallet, userId]);

  const refreshPlanTier = useCallback(async () => {
    if (!userId) {
      planTierRef.current = null;
      setPlanTier(null);
      return;
    }
    try {
      // Same derivation as deriveCurrentTier (creditWalletRpc.js): credit_tier
      // first, plan_name fallback. Failures keep the previous tier rather
      // than flashing "no denominator".
      const { data, error } = await supabase
        .from('subscription_info')
        .select('plan_name, credit_tier')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (currentUserIdRef.current !== userId) return;
      const tier = data ? ((data.credit_tier || data.plan_name || '').trim().toLowerCase() || null) : null;
      planTierRef.current = tier;
      setPlanTier(tier);
    } catch (error) {
      console.error('Unable to read subscription tier:', error);
    }
  }, [userId]);

  useEffect(() => {
    // Do not show a previous therapist's balance while a new login is loading.
    walletRef.current = null;
    setWallet(null);
    planTierRef.current = null;
    setPlanTier(null);
  }, [userId]);

  useEffect(() => {
    refreshWallet();
    refreshPlanTier();
  }, [refreshWallet, refreshPlanTier]);

  useEffect(() => {
    const refreshOnFocus = () => { refreshWallet(); refreshPlanTier(); };
    // A wallet operation (purchase, recharge) may also change the tier, so
    // refresh both on the same event.
    const refreshOnOperationCompletion = () => { refreshWallet(); refreshPlanTier(); };
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('credit-wallet-operation-complete', refreshOnOperationCompletion);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('credit-wallet-operation-complete', refreshOnOperationCompletion);
    };
  }, [refreshWallet, refreshPlanTier]);

  useEffect(() => {
    if (!userId) return undefined;
    const channel = supabase
      .channel(`credit-wallet:${userId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'therapist_credit_wallets', filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (payload.new?.user_id === currentUserIdRef.current) acceptWallet(payload.new);
        else refreshWallet();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [acceptWallet, refreshWallet, userId]);

  return (
    <WalletBalanceContext.Provider value={{ wallet, planTier, loading, refreshWallet }}>
      {children}
    </WalletBalanceContext.Provider>
  );
};

export const useWalletBalance = () => useContext(WalletBalanceContext);

export const walletCredits = (wallet) => (wallet ? Number(wallet.balance_halves) / 2 : null);

// The plan's REMAINING plan allotment this cycle (not the monthly allowance):
// allotment_halves is drawn down by every plan-funded charge, and
// balance_halves = allotment_halves + protected_halves - debt_halves. It is
// NOT a denominator for "X of Y" meters — see walletPlanAllotmentCredits.
export const walletAllotmentCredits = (wallet) =>
  (wallet && Number(wallet.allotment_halves) > 0 ? Number(wallet.allotment_halves) / 2 : null);

// The plan's monthly credit allowance for a tier ('free' | 'basic' | 'pro'),
// or null when the tier is unknown — callers that draw a proportion must
// treat null as "no denominator" rather than as zero, which would read as a
// permanently empty meter.
export const walletPlanAllotmentCredits = (tier) => {
  if (!tier) return null;
  const plan = Object.values(SUBSCRIPTION_PLANS).find((p) => p.tier === tier)
    || Object.values(SUBSCRIPTION_PLANS).find((p) => p.tier === 'free');
  return plan?.monthlyCredits ?? null;
};
